import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Trophy,
  Loader2,
  RefreshCw,
  Maximize2,
  Minimize2,
  CheckCircle2,
  Search,
  Package,
  Radio,
} from "lucide-react";
import { toast } from "react-toastify";
import LiveContestSidebar from "components/LiveContestSidebar";
import ContestProductControls from "components/ContestProductControls";
import { AnimatedLiveCounter } from "components/AnimatedLiveCounter";
import {
  formatLeaderboardTime,
  shouldShowLeaderboardQuestions,
} from "utils/prizeDisplay";
import {
  useGetProductsQuery,
  useGetProductWishesQuery,
  useGetContestLiveStatsQuery,
  useGetPlayTableHistoryQuery,
  useCompleteProductWishMutation,
  useSetProductActiveStatusMutation,
  useSetProductPlayableStatusMutation,
  api,
} from "state/api";
import useLiveContestRealtime from "hooks/useLiveContestRealtime";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

const PAGE_SIZE = 25;

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN");
  } catch {
    return String(d);
  }
};

const statusClass = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "ONGOING")
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
  if (s === "PENDINGRESULT" || s === "PENDINGRESULT_EVENT")
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
  if (s === "PROCESSED" || s === "COMPLETED" || s === "DONE")
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
  return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
};

const payoutStatusClass = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "PAID")
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
  if (s === "PENDING" || s === "AWAITING_RESULT")
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
  if (s === "FAILED")
    return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  if (s === "PRODUCT")
    return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
};

const payoutStatusLabel = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "PAID") return "Paid";
  if (s === "PENDING") return "Pending payout";
  if (s === "AWAITING_RESULT") return "Awaiting result";
  if (s === "FAILED") return "Failed";
  if (s === "PRODUCT") return "Product prize";
  if (s === "NOT_APPLICABLE") return "N/A";
  return status || "—";
};

const userLabel = (entry) => {
  const u = entry.userDetails;
  if (!u) return entry.userId || "—";
  const name = [u.name, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.phone || entry.userId;
};

const isOngoing = (wish) => (wish?.status || "").toUpperCase() === "ONGOING";

const pickDefaultWish = (wishes) => {
  if (!wishes?.length) return null;
  return (
    wishes.find((w) => w.isCurrentRound) ||
    wishes.find((w) => isOngoing(w)) ||
    wishes[0]
  );
};

const LeaderboardTable = ({ history, loading, pageFetching = false, error, emptyMessage }) => {
  if (loading || pageFetching) {
    return (
      <div className="flex flex-col justify-center items-center flex-1 min-h-[200px] gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        {pageFetching && (
          <p className="text-xs text-gray-500 font-medium">Loading next page…</p>
        )}
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400 text-center py-8">
        Failed to load leaderboard.
      </p>
    );
  }
  if (!history?.length) {
    return (
      <p className="text-sm text-gray-500 text-center py-8">{emptyMessage}</p>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10">
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Player</TableHead>
            <TableHead className="text-center">Correct</TableHead>
            <TableHead className="text-center">Time</TableHead>
            <TableHead className="text-center">Payout</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((row) => (
            <TableRow key={row._id}>
              <TableCell className="font-medium">
                {row.rank === 0 ? "#" : row.rank}
              </TableCell>
              <TableCell>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{userLabel(row)}</p>
                  {row.userDetails?.phone && (
                    <p className="text-xs text-gray-500 font-mono truncate">
                      {row.userDetails.phone}
                    </p>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-center">
                {shouldShowLeaderboardQuestions(row) ? (
                  <>
                    {row.correctQuestion ?? "—"}/{row.totalQuestion ?? "—"}
                  </>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className="text-center text-xs">
                {formatLeaderboardTime(row)}
              </TableCell>
              <TableCell className="text-center">
                {row.payoutStatus && row.payoutStatus !== "NOT_APPLICABLE" ? (
                  <div className="flex flex-col items-center gap-0.5">
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${payoutStatusClass(row.payoutStatus)}`}
                    >
                      {payoutStatusLabel(row.payoutStatus)}
                    </span>
                    {row.payoutAmount && (
                      <span className="text-xs text-gray-500">
                        ₹{row.payoutAmount}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const RoundHeader = ({
  product,
  selectedWish,
  totalCount,
  uniqueUserCount,
  payoutSummary,
  onRefresh,
  refreshing,
  onComplete,
  onFullscreen,
  onOpenLiveDashboard,
  onOpenLiveMobile,
  fullscreen,
  liveRealtimeConnected,
  productActive,
  onToggleProductActive,
  togglingProductStatus,
  productPlayable,
  onToggleProductPlayable,
  togglingPlayableStatus,
}) => {
  const payoutEligible = payoutSummary?.totalEligible ?? 0;
  const payoutPaid = payoutSummary?.paid ?? 0;
  const payoutPending = payoutSummary?.pending ?? 0;
  const payoutFailed = payoutSummary?.failed ?? 0;
  const payoutProgressPct =
    payoutEligible > 0 ? Math.round((payoutPaid / payoutEligible) * 100) : 0;

  if (!product || !selectedWish) {
    return (
      <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <p className="text-sm text-gray-500">Select a product and round to view leaderboard.</p>
      </div>
    );
  }

  return (
    <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-indigo-600 shrink-0" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {product.productName}
            </h2>
          </div>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClass(selectedWish.status)}`}
              >
                {selectedWish.status}
              </span>
              {selectedWish.isCurrentRound && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-medium">
                  Current round
                </span>
              )}
              {liveRealtimeConnected && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums leading-none">
                <AnimatedLiveCounter
                  value={selectedWish.fillWish ?? 0}
                  size="md"
                  className="!text-2xl"
                />
                {product?.totalWish ? (
                  <span className="text-lg font-semibold text-gray-500">
                    {" "}
                    / {Number(product.totalWish).toLocaleString("en-IN")}
                  </span>
                ) : null}
              </p>
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-1">
                entries · wishes used to join
              </p>
            </div>
            <div className="flex items-start gap-8">
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums leading-none">
                  <AnimatedLiveCounter
                    value={uniqueUserCount}
                    size="md"
                    className="!text-2xl"
                  />
                </p>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-1">
                  unique users
                </p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums leading-none">
                  <AnimatedLiveCounter value={totalCount} size="md" className="!text-2xl" />
                </p>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-1">
                  on leaderboard
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isOngoing(selectedWish) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => onComplete(selectedWish)}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Mark Pending Result
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            className="h-8"
          >
            {refreshing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenLiveDashboard}
            className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-50"
            title="Open live dashboard for this contest"
          >
            <Radio className="h-3.5 w-3.5 mr-1" />
            Live Dashboard
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenLiveMobile}
            className="h-8 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
            title="Open mobile live leaderboard for this contest"
          >
            <Radio className="h-3.5 w-3.5 mr-1" />
            Live Mobile
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onFullscreen}
            className="h-8"
            title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
          >
            {fullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      {(onToggleProductActive || onToggleProductPlayable) && (
        <div className="mt-3 max-w-md">
          <ContestProductControls
            productActive={productActive}
            onToggleProductActive={onToggleProductActive}
            togglingProductStatus={togglingProductStatus}
            productPlayable={productPlayable}
            onToggleProductPlayable={onToggleProductPlayable}
            togglingPlayableStatus={togglingPlayableStatus}
          />
        </div>
      )}
      {payoutEligible > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>Payout progress</span>
            <span>
              {payoutPaid}/{payoutEligible} paid
              {payoutPending > 0 ? ` · ${payoutPending} pending` : ""}
              {payoutFailed > 0 ? ` · ${payoutFailed} failed` : ""}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-green-500 transition-all duration-300"
              style={{ width: `${payoutProgressPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const PrizeLeaderboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedWish, setSelectedWish] = useState(null);
  const [historyPage] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [liveRefreshTick, setLiveRefreshTick] = useState(0);
  const [liveEntryCount, setLiveEntryCount] = useState(null);
  const [liveUniqueUserCount, setLiveUniqueUserCount] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const lastEntryCountRef = useRef(null);
  const lastUniqueUserCountRef = useRef(null);
  const lastFillWishRef = useRef(null);
  const historyRefetchTimerRef = useRef(null);

  const isLiveRound =
    Boolean(selectedProductId && selectedWish?.playToken) &&
    isOngoing(selectedWish);

  const { data: productsResponse, isLoading: productsLoading } = useGetProductsQuery();
  const products = productsResponse?.data || productsResponse?.products || productsResponse || [];

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.productName?.toLowerCase().includes(q) ||
        p._id?.toLowerCase().includes(q)
    );
  }, [products, search]);

  const selectedProduct = useMemo(
    () => products.find((p) => p._id === selectedProductId) || null,
    [products, selectedProductId]
  );

  const {
    data: wishesData,
    isLoading: wishesLoading,
    isFetching: wishesFetching,
    refetch: refetchWishes,
  } = useGetProductWishesQuery(selectedProductId, {
    skip: !selectedProductId,
    // Poll while an ongoing round is selected (fallback if WebSocket is down)
    refetchInterval: isLiveRound ? 5000 : false,
  });

  const { data: liveStatsData } = useGetContestLiveStatsQuery(
    {
      productId: selectedProductId,
      playToken: selectedWish?.playToken,
    },
    {
      skip: !isLiveRound,
      pollingInterval: 3000,
    }
  );

  const wishes = wishesData?.productWishDocuments ?? [];

  const displayWishes = useMemo(() => {
    if (!selectedWish?._id) return wishes;
    return wishes.map((wish) =>
      wish._id === selectedWish._id
        ? {
            ...wish,
            fillWish: Math.max(
              selectedWish.fillWish ?? 0,
              wish.fillWish ?? 0
            ),
            entryCount: Math.max(
              selectedWish.entryCount ?? 0,
              wish.entryCount ?? 0
            ),
            uniqueUserCount: Math.max(
              selectedWish.uniqueUserCount ?? 0,
              wish.uniqueUserCount ?? 0
            ),
            status: selectedWish.status ?? wish.status,
          }
        : wish
    );
  }, [wishes, selectedWish]);

  const {
    data: historyData,
    isLoading: historyLoading,
    isFetching: historyFetching,
    error: historyError,
    refetch: refetchHistory,
  } = useGetPlayTableHistoryQuery(
    {
      productId: selectedProductId,
      playToken: selectedWish?.playToken,
      page: historyPage,
      pageSize: PAGE_SIZE,
    },
    {
      skip: !selectedProductId || !selectedWish?.playToken,
    }
  );

  const [completeProductWish, { isLoading: completingWish }] =
    useCompleteProductWishMutation();
  const [setProductActiveStatus, { isLoading: togglingProductStatus }] =
    useSetProductActiveStatusMutation();
  const [setProductPlayableStatus, { isLoading: togglingPlayableStatus }] =
    useSetProductPlayableStatusMutation();

  const history = historyData?.history ?? [];
  const historyTotalCount = historyData?.totalCount ?? 0;
  const historyUniqueUserCount = historyData?.uniqueUserCount;
  const totalCount = liveEntryCount ?? historyTotalCount;
  const uniqueUserCount =
    liveUniqueUserCount ??
    historyUniqueUserCount ??
    selectedWish?.uniqueUserCount ??
    0;
  const payoutSummary = historyData?.payoutSummary;

  useEffect(() => {
    setLiveEntryCount(null);
    setLiveUniqueUserCount(null);
    lastEntryCountRef.current = null;
    lastUniqueUserCountRef.current = null;
    lastFillWishRef.current = null;
  }, [selectedWish?.playToken, selectedProductId]);

  useEffect(
    () => () => {
      if (historyRefetchTimerRef.current) {
        clearTimeout(historyRefetchTimerRef.current);
      }
    },
    []
  );

  const scheduleLeaderboardRefetch = useCallback(
    (productId, playToken) => {
      if (historyRefetchTimerRef.current) {
        clearTimeout(historyRefetchTimerRef.current);
      }
      historyRefetchTimerRef.current = setTimeout(() => {
        dispatch(
          api.endpoints.getPlayTableHistory.initiate(
            {
              productId,
              playToken,
              page: historyPage,
              pageSize: PAGE_SIZE,
            },
            { forceRefetch: true }
          )
        );
      }, 800);
    },
    [historyPage, dispatch]
  );

  const applyLiveStats = useCallback(
    (payload) => {
      if (!payload?.productId || !payload?.playToken) return;
      if (String(payload.productId) !== String(selectedProductId)) return;
      if (payload.message) return;

      if (
        lastFillWishRef.current !== null &&
        lastFillWishRef.current === payload.fillWish &&
        lastEntryCountRef.current === payload.entryCount &&
        lastUniqueUserCountRef.current === payload.uniqueUserCount
      ) {
        return;
      }

      setLiveEntryCount(payload.entryCount);
      setLiveUniqueUserCount(payload.uniqueUserCount ?? null);
      setSelectedWish((prev) => {
        if (!prev || prev.playToken !== payload.playToken) return prev;
        return {
          ...prev,
          fillWish: payload.fillWish,
          entryCount: payload.entryCount,
          uniqueUserCount: payload.uniqueUserCount ?? prev.uniqueUserCount,
          status: payload.status ?? prev.status,
        };
      });

      dispatch(
        api.util.updateQueryData("getProductWishes", selectedProductId, (draft) => {
          const wish = draft?.productWishDocuments?.find(
            (w) => w.playToken === payload.playToken
          );
          if (wish) {
            wish.fillWish = payload.fillWish;
            wish.entryCount = payload.entryCount;
            wish.uniqueUserCount = payload.uniqueUserCount ?? wish.uniqueUserCount;
            if (payload.status) wish.status = payload.status;
          }
        })
      );

      const entryCountChanged =
        lastEntryCountRef.current !== null &&
        payload.entryCount !== lastEntryCountRef.current;
      const uniqueUserCountChanged =
        lastUniqueUserCountRef.current !== null &&
        payload.uniqueUserCount !== lastUniqueUserCountRef.current;
      lastEntryCountRef.current = payload.entryCount;
      lastUniqueUserCountRef.current = payload.uniqueUserCount ?? null;
      lastFillWishRef.current = payload.fillWish;

      if (entryCountChanged || uniqueUserCountChanged) {
        scheduleLeaderboardRefetch(payload.productId, payload.playToken);
      }
    },
    [selectedProductId, dispatch, scheduleLeaderboardRefetch]
  );

  const handleLiveContestUpdate = useCallback(
    (payload) => applyLiveStats(payload),
    [applyLiveStats]
  );

  useEffect(() => {
    if (liveStatsData && !liveStatsData.message) {
      applyLiveStats(liveStatsData);
    }
  }, [liveStatsData, applyLiveStats]);

  const { connected: liveRealtimeConnected } = useLiveContestRealtime({
    productId: selectedProductId,
    playToken: selectedWish?.playToken,
    enabled: Boolean(selectedProductId && selectedWish?.playToken),
    onUpdate: handleLiveContestUpdate,
  });

  useEffect(() => {
    const restoreProductId = location.state?.productId;
    if (restoreProductId) {
      setSelectedProductId(restoreProductId);
    }
  }, [location.state?.productId]);

  useEffect(() => {
    if (!selectedProductId && filteredProducts.length > 0) {
      setSelectedProductId(filteredProducts[0]._id);
    }
  }, [filteredProducts, selectedProductId]);

  useEffect(() => {
    if (!selectedProductId) {
      setSelectedWish(null);
      return;
    }
    if (wishes.length === 0) {
      setSelectedWish(null);
      return;
    }
    const restorePlayToken = location.state?.playToken;
    setSelectedWish((prev) => {
      if (restorePlayToken) {
        const restored = wishes.find((w) => w.playToken === restorePlayToken);
        if (restored) return restored;
      }
      if (prev && wishes.some((w) => w._id === prev._id)) {
        const fromApi = wishes.find((w) => w._id === prev._id) || prev;
        return {
          ...fromApi,
          fillWish: Math.max(fromApi.fillWish ?? 0, prev.fillWish ?? 0),
          entryCount: Math.max(fromApi.entryCount ?? 0, prev.entryCount ?? 0),
          uniqueUserCount: Math.max(
            fromApi.uniqueUserCount ?? 0,
            prev.uniqueUserCount ?? 0
          ),
        };
      }
      return pickDefaultWish(wishes);
    });
  }, [selectedProductId, wishes, location.state?.playToken]);

  const handleToggleProductActive = useCallback(
    async (active) => {
      if (!selectedProductId) return;
      try {
        await setProductActiveStatus({ productId: selectedProductId, active }).unwrap();
        toast.success(active ? "Contest is now active" : "Contest is now inactive", {
          position: "top-right",
        });
      } catch (err) {
        toast.error(err?.data?.message || "Failed to update contest status", {
          position: "top-right",
        });
      }
    },
    [selectedProductId, setProductActiveStatus]
  );

  const handleToggleProductPlayable = useCallback(
    async (playable) => {
      if (!selectedProductId) return;
      try {
        await setProductPlayableStatus({ productId: selectedProductId, playable }).unwrap();
        toast.success(playable ? "Product is now playable (YES)" : "Product is now not playable (NO)", {
          position: "top-right",
        });
      } catch (err) {
        toast.error(err?.data?.message || "Failed to update isPlayable", {
          position: "top-right",
        });
      }
    },
    [selectedProductId, setProductPlayableStatus]
  );

  const productActive = (selectedProduct?.status || "").toUpperCase() === "ACTIVE";
  const productPlayable = (selectedProduct?.isPlayable || "").toUpperCase() === "YES";

  const exitFullscreen = useCallback(() => setFullscreen(false), []);

  const openLiveDashboard = useCallback(() => {
    if (!selectedProductId || !selectedWish?.playToken) {
      toast.info("Select a contest round first", { position: "top-right" });
      return;
    }
    setFullscreen(false);
    navigate(
      `/prize-leaderboard/live/${selectedProductId}/${encodeURIComponent(selectedWish.playToken)}`
    );
  }, [selectedProductId, selectedWish, navigate]);

  const openLiveMobile = useCallback(() => {
    if (!selectedProductId || !selectedWish?.playToken) {
      toast.info("Select a contest round first", { position: "top-right" });
      return;
    }
    setFullscreen(false);
    navigate(
      `/prize-leaderboard/live-mobile/${selectedProductId}/${encodeURIComponent(selectedWish.playToken)}`
    );
  }, [selectedProductId, selectedWish, navigate]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        exitFullscreen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fullscreen, exitFullscreen]);

  useEffect(() => {
    document.body.style.overflow = fullscreen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [fullscreen]);

  const handleConfirmComplete = async () => {
    if (!completeTarget?._id || !selectedProductId) return;
    try {
      const result = await completeProductWish({
        productWishId: completeTarget._id,
        productId: selectedProductId,
      }).unwrap();
      toast.success(
        result.playTokenRotated
          ? "Round marked PENDINGRESULT. Product playToken rotated and a new ONGOING round was created."
          : "Round marked PENDINGRESULT.",
        { position: "top-right" }
      );
      if (selectedWish?._id === completeTarget._id) {
        setSelectedWish(null);
      }
      setCompleteTarget(null);
      refetchWishes();
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Failed to complete round",
        { position: "top-right" }
      );
    }
  };

  const leaderboardPanel = (
    <div className="flex flex-col min-h-0 flex-1 overflow-hidden bg-white dark:bg-gray-900">
      <RoundHeader
        product={selectedProduct}
        selectedWish={selectedWish}
        totalCount={totalCount}
        uniqueUserCount={uniqueUserCount}
        payoutSummary={payoutSummary}
        onRefresh={() => {
          refetchWishes();
          refetchHistory();
          setLiveRefreshTick((tick) => tick + 1);
        }}
        refreshing={historyFetching || wishesFetching}
        onComplete={setCompleteTarget}
        onFullscreen={() => setFullscreen((v) => !v)}
        onOpenLiveDashboard={openLiveDashboard}
        onOpenLiveMobile={openLiveMobile}
        fullscreen={fullscreen}
        liveRealtimeConnected={liveRealtimeConnected}
        productActive={productActive}
        onToggleProductActive={selectedProduct ? handleToggleProductActive : undefined}
        togglingProductStatus={togglingProductStatus}
        productPlayable={productPlayable}
        onToggleProductPlayable={selectedProduct ? handleToggleProductPlayable : undefined}
        togglingPlayableStatus={togglingPlayableStatus}
      />
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <LiveContestSidebar
          embedded
          product={selectedProduct}
          selectedWish={selectedWish}
          fallbackHistory={
            historyData?.message === "No matching play table history found"
              ? []
              : history
          }
          fallbackLoading={historyLoading && history.length === 0}
          refreshTrigger={liveRefreshTick}
          realtimeConnected={liveRealtimeConnected}
        />
      </div>
    </div>
  );

  const pageContent = (
    <div className="h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
      <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-indigo-600" />
              Prize Leaderboard
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Product-wise live rankings — select a product and round
            </p>
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-12 overflow-hidden">
        {/* Products */}
        <div className="col-span-12 lg:col-span-3 border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0 overflow-hidden bg-white dark:bg-gray-900">
          <div className="shrink-0 px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-xs font-medium text-gray-500 uppercase tracking-wide">
            Products ({filteredProducts.length})
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {productsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8 px-3">No products found.</p>
            ) : (
              filteredProducts.map((product) => {
                const active = product._id === selectedProductId;
                return (
                  <button
                    key={product._id}
                    type="button"
                    onClick={() => {
                      setSelectedProductId(product._id);
                      setSelectedWish(null);
                    }}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      active
                        ? "bg-indigo-50 dark:bg-indigo-950/40 border-l-2 border-l-indigo-600"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Package className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {product.productName}
                        </p>
                        <span
                          className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${
                            product.isPlayable === "YES"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {product.isPlayable === "YES" ? "Playable" : "Not playable"}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Rounds */}
        <div className="col-span-12 lg:col-span-3 border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0 overflow-hidden bg-gray-50/50 dark:bg-gray-900/50">
          <div className="shrink-0 px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Rounds
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => refetchWishes()}
              disabled={!selectedProductId || wishesFetching}
            >
              {wishesFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2">
            {!selectedProductId ? (
              <p className="text-sm text-gray-500 text-center py-8">Select a product.</p>
            ) : wishesLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : wishes.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No rounds yet.</p>
            ) : (
              displayWishes.map((wish) => {
                const active = selectedWish?._id === wish._id;
                return (
                  <div
                    key={wish._id}
                    className={`rounded-lg border overflow-hidden ${
                      active
                        ? "border-indigo-400 ring-1 ring-indigo-200 dark:ring-indigo-800"
                        : "border-gray-200 dark:border-gray-700"
                    } bg-white dark:bg-gray-900`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedWish(wish)}
                      className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClass(wish.status)}`}
                        >
                          {wish.status}
                        </span>
                        {wish.isCurrentRound && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 font-medium">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-bold text-gray-900 dark:text-white mt-2 tabular-nums leading-none">
                        {active && isOngoing(wish) ? (
                          <AnimatedLiveCounter
                            value={wish.fillWish ?? 0}
                            size="md"
                            className="!text-xl"
                          />
                        ) : (
                          (wish.fillWish ?? 0).toLocaleString("en-IN")
                        )}
                        {wishesData?.product?.totalWish ? (
                          <span className="text-base font-semibold text-gray-500">
                            {" "}
                            / {Number(wishesData.product.totalWish).toLocaleString("en-IN")}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-0.5">
                        entries · wishes used to join
                      </p>
                      <div className="flex items-start gap-5 mt-2">
                        <div>
                          <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums leading-none">
                            {active && isOngoing(wish) ? (
                              <AnimatedLiveCounter
                                value={wish.uniqueUserCount ?? 0}
                                size="md"
                                className="!text-xl"
                              />
                            ) : (
                              (wish.uniqueUserCount ?? 0).toLocaleString("en-IN")
                            )}
                          </p>
                          <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mt-0.5">
                            unique users
                          </p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums leading-none">
                            {active && isOngoing(wish) ? (
                              <AnimatedLiveCounter
                                value={wish.entryCount ?? 0}
                                size="md"
                                className="!text-xl"
                              />
                            ) : (
                              (wish.entryCount ?? 0).toLocaleString("en-IN")
                            )}
                          </p>
                          <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mt-0.5">
                            on leaderboard
                          </p>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {formatDate(wish.updatedAt || wish.timestamp)}
                      </p>
                    </button>
                    {isOngoing(wish) && (
                      <div className="px-3 pb-3 space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => setCompleteTarget(wish)}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          Mark Pending Result
                        </Button>
                        {active && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full text-xs border-red-300 text-red-700 hover:bg-red-50"
                              onClick={openLiveDashboard}
                            >
                              <Radio className="h-3.5 w-3.5 mr-1" />
                              Live Dashboard
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                              onClick={openLiveMobile}
                            >
                              <Radio className="h-3.5 w-3.5 mr-1" />
                              Live Mobile
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                    {!isOngoing(wish) && active && (
                      <div className="px-3 pb-3 space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-xs border-red-300 text-red-700 hover:bg-red-50"
                          onClick={openLiveDashboard}
                        >
                          <Radio className="h-3.5 w-3.5 mr-1" />
                          Live Dashboard
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                          onClick={openLiveMobile}
                        >
                          <Radio className="h-3.5 w-3.5 mr-1" />
                          Live Mobile
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="col-span-12 lg:col-span-6 flex flex-col min-h-0 overflow-hidden">
          {leaderboardPanel}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {pageContent}

      {fullscreen &&
        createPortal(
          <div className="fixed inset-0 z-[9998] flex flex-col bg-white dark:bg-gray-950 overflow-hidden w-screen h-[100dvh]">
            <div className="shrink-0 px-4 py-2.5 bg-indigo-600 text-white flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold truncate">
                  {selectedProduct?.productName || "Leaderboard"}
                </h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => {
                    refetchWishes();
                    refetchHistory();
                  }}
                  disabled={historyFetching || wishesFetching}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 mr-1.5 ${historyFetching || wishesFetching ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={exitFullscreen}
                >
                  <Minimize2 className="h-3.5 w-3.5 mr-1.5" />
                  Exit
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{leaderboardPanel}</div>
          </div>,
          document.body
        )}

      <Dialog
        open={!!completeTarget}
        onOpenChange={(isOpen) => {
          if (!isOpen) setCompleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark competition PENDINGRESULT?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  This will close the ONGOING round and set its status to{" "}
                  <strong>PENDINGRESULT</strong>.
                </p>
                {completeTarget?.isCurrentRound ? (
                  <p>
                    This is the <strong>current</strong> round — the product will stop accepting
                    new entries, and a new ONGOING round will start.
                  </p>
                ) : (
                  <p>This is an older round — only its status will change.</p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmComplete} disabled={completingWish}>
              {completingWish ? "Updating..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PrizeLeaderboard;
